import { useAuthUser } from "@/context/auth/AuthContext";
import { can } from "@/context/auth/authz";
import { resourceForRoute } from "@/context/auth/routeResources";
import { usePlanAccess } from "./usePlanAccess";

export interface Permission {
  path: string;
}

export interface PermissionsResult {
  canRead: boolean;
  canModify: boolean;
  canDelete: boolean;
  isOwner: boolean;
  hasFeatureAccess: boolean;
  planAccessReason?:
    | "plan_feature"
    | "plan_status"
    | "no_plan"
    | "trial_expired"
    | "no_features";
  requiredFeature?: string;
  isInTrial?: boolean;
}

/**
 * Permisos de la ruta actual. El grant ahora lo decide `can()` — el vocab
 * canónico del entitlement (view/modify/delete sobre resource ids) — resolviendo
 * el string de ruta a su recurso vía `resourceForRoute`. Antes buscaba en
 * `user.paths` (derivado con pérdida de `paths.ts`, 38 ids vs 46 del entitlement),
 * que era la fuente de la divergencia (#49).
 *
 * El plan/trial sigue saliendo de `usePlanAccess`, pero **como señal comercial**
 * (`hasFeatureAccess`/`planAccessReason`, para el upsell), no como authz: al
 * owner no le saca el CRUD (#122).
 *
 * NOTA: sigue tomando un string de ruta por compatibilidad con las ~33 páginas que
 * la llaman así; el destino es que cada una pida `useCan(RESOURCES.X, action)` y
 * este hook + `routeResources` desaparezcan (extracción a @trud/auth).
 */
function usePermissions(
  urlProp: string,
  planFeature?: string | string[]
): PermissionsResult {
  const user = useAuthUser();
  const planAccess = usePlanAccess(urlProp, planFeature);
  const isOwner = !!(user && user.type === "owner");

  // Plan primero: si el negocio no tiene el feature, se niega todo —
  // MENOS AL OWNER.
  //
  // Este corte estaba antes de `can()` y sin excepción, así que un owner cuyo
  // negocio no tuviera el plan en `active` (el disparador típico es
  // `trial_expired`) entraba al módulo —el sidebar y `can()` sí lo bypassean—
  // y adentro no veía NINGÚN botón de crear ni editar. En todo el ERP (#122).
  //
  // La doctrina ya estaba escrita en `context/auth/authz.ts`: la autorización
  // se decide con `modules` + rol/grant y NUNCA con el estado del plan. El eje
  // comercial es otra cosa y viaja aparte, en `hasFeatureAccess` /
  // `planAccessReason`, que se siguen devolviendo TAL CUAL más abajo: el
  // upsell (`NoAccessPlan` vía `ClientLayout`) aparece igual que antes, y para
  // el que no es owner no cambia nada.
  if (!planAccess.hasAccess && !isOwner) {
    return {
      canRead: false,
      canModify: false,
      canDelete: false,
      isOwner,
      hasFeatureAccess: false,
      planAccessReason: planAccess.reason,
      requiredFeature: planAccess.requiredFeature,
      isInTrial: planAccess.isInTrial,
    };
  }

  // Rutas owner-only: sólo el owner.
  if (urlProp === "owner") {
    return {
      canRead: isOwner,
      canModify: isOwner,
      canDelete: isOwner,
      isOwner,
      hasFeatureAccess: planAccess.hasAccess,
      planAccessReason: planAccess.reason,
      requiredFeature: planAccess.requiredFeature,
      isInTrial: planAccess.isInTrial,
    };
  }

  // Grant por recurso. `resourceForRoute` devuelve `undefined` para rutas sin
  // recurso (home "/", desconocidas) → `can()` las trata como ALLOW (ungated).
  const resource = resourceForRoute(urlProp);
  return {
    canRead: can(user, resource, "view"),
    canModify: can(user, resource, "modify"),
    canDelete: can(user, resource, "delete"),
    isOwner,
    hasFeatureAccess: planAccess.hasAccess,
    planAccessReason: planAccess.reason,
    requiredFeature: planAccess.requiredFeature,
    isInTrial: planAccess.isInTrial,
  };
}

export default usePermissions;
